import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { skinSeoulProductsClient } from '../../../infrastructure/SkinSeoulProductsClient';
import { cpSearchClient } from '../../../infrastructure/CpSearchClient';
import { useDebouncedCallback } from '../common/useDebouncedCallback';
import type { SkinSeoulAdminProduct, PublicationStatus, StockStatus } from '../../../models/ProductTypes';
import type { GpKeywordCpSearch } from '../../../models/Coupang';
import { SkinSeoulProductType } from '../../../models/ProductTypes';

// Helper function to sort Coupang products
const sortCoupangProducts = (products: GpKeywordCpSearch[]): GpKeywordCpSearch[] => {
  return products.sort((a, b) => {
    // First priority: Search rank (ascending - lower rank number = better position)
    const aRanking = a.cpSearchedProduct.ranking;
    const bRanking = b.cpSearchedProduct.ranking;

    if (aRanking !== undefined && bRanking !== undefined) {
      if (aRanking !== bRanking) {
        return aRanking - bRanking;
      }
    } else if (aRanking !== undefined) {
      return -1; // a has ranking, b doesn't - a comes first
    } else if (bRanking !== undefined) {
      return 1; // b has ranking, a doesn't - b comes first
    }

    // Second priority: Rocket delivery
    const aIsRocket =
      a.cpSearchedProduct.shippingInfo?.shippingType === 'rocket' || a.cpSearchedProduct.shipping === 'Rocket';
    const bIsRocket =
      b.cpSearchedProduct.shippingInfo?.shippingType === 'rocket' || b.cpSearchedProduct.shipping === 'Rocket';

    if (aIsRocket && !bIsRocket) {
      return -1;
    }
    if (!aIsRocket && bIsRocket) {
      return 1;
    }

    // If all else is equal, maintain original order
    return 0;
  });
};

interface Filters {
  postStatus: PublicationStatus | 'All';
  productType: SkinSeoulProductType | 'All';
  stockStatus: StockStatus | 'All';
  search: string;
  brand: string;
  category: string;
  wave: string;
  sku: string;
}

export const useSkinSeoulRematching = () => {
  const [selectedProduct, setSelectedProduct] = useState<SkinSeoulAdminProduct | null>(null);
  const [selectedVariation, setSelectedVariation] = useState<SkinSeoulAdminProduct | null>(null);
  const [coupangProducts, setCoupangProducts] = useState<GpKeywordCpSearch[]>([]);
  const [coupangLoading, setCoupangLoading] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    postStatus: 'All',
    productType: 'All',
    stockStatus: 'All',
    search: '',
    brand: '',
    category: '',
    wave: 'all',
    sku: '',
  });

  // Separate state for SKU input to show user typing
  const [skuInput, setSkuInput] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
  });

  // Fetch SkinSeoul products with filters
  const {
    data: productsData,
    isLoading,
    refetch,
    error,
  } = useQuery({
    queryKey: ['skinseoul-products', filters, pagination.page, pagination.limit],
    queryFn: async () => {
      // Use SKU search if SKU filter is provided
      if (filters.sku.trim()) {
        const products = await skinSeoulProductsClient.searchProductsBySku(
          filters.sku.trim(),
          filters.postStatus === 'All' ? undefined : filters.postStatus,
        );

        // Return in the same format as getAllProducts
        return {
          products,
          total: products.length,
          page: 1,
          limit: products.length,
        };
      }

      // Otherwise use regular getAllProducts
      return skinSeoulProductsClient.getAllProducts(
        pagination.page,
        pagination.limit,
        filters.postStatus === 'All' ? undefined : filters.postStatus,
        filters.productType === 'All' ? undefined : filters.productType,
        filters.stockStatus === 'All' ? undefined : filters.stockStatus,
      );
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Shared function to handle product(simple/variable)/variation selection
  const handleProductOrVariationSelect = useCallback(async (item: SkinSeoulAdminProduct, isVariation: boolean) => {
    // Clear previous results
    setCoupangProducts([]);

    // Don't call API for Variable products - they will show special message in UI
    if (item.type === SkinSeoulProductType.Variable) {
      console.info('🔍 Skipping Coupang search for Variable product type');
      return;
    }

    // Use item SKU as the search parameter
    const sku = item.sku;
    if (sku) {
      const itemType = isVariation ? 'Variation' : 'Product';
      console.info(`🔍 Searching Coupang products for ${itemType} SKU:`, sku);
      setCoupangLoading(true);

      try {
        // Fetch Coupang keyword search results using SKU as gpProductId
        const response = await cpSearchClient.getGpKeywordCpSearch(sku);

        console.info('🔍 API Response received:', response);
        console.info('🔍 Number of products in response:', response.products?.length || 0);
        console.info('🔍 Raw response.products:', response.products);

        // Check if response.products exists and is an array
        if (!response.products || !Array.isArray(response.products)) {
          console.warn('⚠️ Invalid response.products:', response.products);
          setCoupangProducts([]);
          setCoupangLoading(false);
          return;
        }

        // Transform and sort the results
        const searchResults: GpKeywordCpSearch[] = response.products
          .filter((item: GpKeywordCpSearch) => {
            const hasIds = item.cpProductId && item.cpItemId;
            if (!hasIds) {
              console.warn('🔍 Filtering out product missing IDs:', item);
            }
            return hasIds;
          })
          .map((item: GpKeywordCpSearch) => ({
            ...item,
          }));

        console.info('🔍 Products after filtering:', searchResults.length);
        console.info('🔍 First few filtered products:', searchResults.slice(0, 2));

        // Sort products using the helper function
        const sortedProducts = sortCoupangProducts(searchResults);

        setCoupangProducts(sortedProducts);
        console.info('🔍 State updated with sorted products:', sortedProducts.length);
        console.info('🔍 Sample product for UI:', sortedProducts[0]);

        // Glowpick API calls removed to prevent unnecessary requests

        console.info(`✅ Found ${sortedProducts.length} Coupang products for ${itemType} SKU: ${sku}`);
      } catch (error) {
        console.error('❌ Failed to fetch Coupang products:', error);
        setCoupangProducts([]);
      } finally {
        setCoupangLoading(false);
      }
    } else {
      const itemType = isVariation ? 'variation' : 'product';
      console.warn(`⚠️ Selected ${itemType} does not have a SKU`);
      setCoupangProducts([]);
    }
  }, []);

  // Handle product selection
  const handleProductSelect = useCallback(
    async (product: SkinSeoulAdminProduct) => {
      setSelectedProduct(product);
      setSelectedVariation(null); // Clear variation selection when main product is selected
      await handleProductOrVariationSelect(product, false);
    },
    [handleProductOrVariationSelect],
  );

  // Handle variation selection
  const handleVariationSelect = useCallback(
    async (variation: SkinSeoulAdminProduct) => {
      setSelectedVariation(variation);
      await handleProductOrVariationSelect(variation, true);
    },
    [handleProductOrVariationSelect],
  );

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page when filters change
  }, []);

  // Debounced SKU search that only triggers for valid SKUs
  const debouncedSkuSearch = useDebouncedCallback((sku: string) => {
    // Only search when SKU is empty (to clear) or has exactly 9 digits (valid SKU format)
    if (sku === '' || (sku.length === 9 && /^\d{9}$/.test(sku))) {
      updateFilters({ sku });
    }
  }, 500); // 500ms delay

  // Handle SKU input changes
  const handleSkuInputChange = useCallback(
    (value: string) => {
      setSkuInput(value);
      debouncedSkuSearch(value);
    },
    [debouncedSkuSearch],
  );

  // Clear SKU input and search
  const clearSkuSearch = useCallback(() => {
    setSkuInput('');
    updateFilters({ sku: '' });
  }, [updateFilters]);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback((limit: number) => {
    setPagination(() => ({ page: 1, limit, total: 0 }));
  }, []);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedProduct(null);
    setCoupangProducts([]);
  }, []);

  // Clear Coupang products
  const clearCoupangProducts = useCallback(() => {
    setCoupangProducts([]);
  }, []);

  return {
    // Data
    products: productsData?.products || [],
    totalProducts: productsData?.total || 0,
    selectedProduct,
    selectedVariation,
    filters,
    pagination,
    skuInput,

    // Loading states
    isLoading,
    error,

    // Actions
    handleProductSelect,
    handleVariationSelect,
    updateFilters,
    handleSkuInputChange,
    clearSkuSearch,
    handlePageChange,
    handlePageSizeChange,
    clearSelection,
    refetch,

    // Coupang functionality
    coupangProducts,
    coupangLoading,
    clearCoupangProducts,
  };
};
